import { useEffect, useState } from 'react';
import { ArrowRight, Check, Copy, Mail } from 'lucide-react';
import type { PaigeEmailIntake } from './paige.global';
import { trackProduct } from './product-tracker';

/**
 * The job's inbound email address in the setup map's "How it works" column, in the map's own row
 * language: the address with a copy button, and how to send a test. Renders nothing when the
 * workbench is not on a Paige job (the bridge has no intake).
 */
export function EmailIntakeCard() {
  const intake = useEmailIntake();
  const [copied, setCopied] = useState(false);
  if (!intake) return null;
  const copy = async () => {
    await navigator.clipboard.writeText(intake.address);
    trackProduct('source.email_copied');
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };
  if (!intake.enabled) return <EmailOff />;
  return (
    <div className={`setup-map-setting paige-email-intake${intake.held ? ' attention' : ''}`}>
      <Mail size={22} aria-hidden="true" />
      <span>
        <strong>Email documents in</strong>
        <small>{stateLine(intake)}</small>
        <span className="paige-email-intake-address">
          <code>{intake.address}</code>
          <button
            type="button"
            className="quiet-button"
            onClick={() => void copy()}
            aria-label="Copy the email address"
          >
            {copied ? (
              <Check size={14} aria-hidden="true" />
            ) : (
              <Copy size={14} aria-hidden="true" />
            )}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </span>
        <small>{testInstructions}</small>
        <EmailSettingsLink />
      </span>
    </div>
  );
}

/** Switched off in the email window: no address to hand out, and the way to switch it back on. */
function EmailOff() {
  return (
    <div className="setup-map-setting paige-email-intake">
      <Mail size={22} aria-hidden="true" />
      <span>
        <strong>Email documents in</strong>
        <small>Off: mail to this job is refused. Switch it on in the email settings.</small>
        <EmailSettingsLink />
      </span>
    </div>
  );
}

const testInstructions =
  'Send a test: attach a PDF or an image and email it from your own mailbox. It arrives here as a ' +
  'new document within a minute; ZIP files and forwarded emails are opened for what is inside.';

/** The address from Paige's bridge, once per mount; null while loading, without a bridge, or when the job has none. */
export function useEmailIntake(): PaigeEmailIntake | null {
  const bridge = window.paigeWorkbench;
  const [intake, setIntake] = useState<PaigeEmailIntake | null>(null);
  useEffect(() => {
    if (!bridge?.emailIntake) return;
    let live = true;
    bridge
      .emailIntake()
      .then((source) => live && setIntake(source))
      .catch(() => live && setIntake(null));
    return () => {
      live = false;
    };
  }, [bridge]);
  return intake;
}

function stateLine(intake: PaigeEmailIntake): string {
  if (intake.held) return 'Held: email to this address is waiting to be released';
  if (!intake.lastActivityAt) return 'Nothing received yet';
  return `Last email ${new Date(intake.lastActivityAt).toLocaleString()}`;
}

/** Where the card leads: the email window, the one place a job's email is set up (Add pages, Email). */
function EmailSettingsLink() {
  const show = window.paigeWorkbench?.showIntake;
  if (!show) return null;
  return (
    <button type="button" className="quiet-button paige-all-sources" onClick={() => show('email')}>
      Email settings
      <ArrowRight size={14} aria-hidden="true" />
    </button>
  );
}
